import type { InventoryVariant } from '@/lib/api/schemas/inventory.schema'
import { SectionCard, ValueOrReason } from '@/components/warehouse/primitives'

const BLOCK_TAGS = new Set([
  'address', 'article', 'aside', 'blockquote', 'br', 'div', 'dl', 'dt', 'dd',
  'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4',
  'h5', 'h6', 'header', 'hr', 'li', 'main', 'nav', 'ol', 'p', 'pre', 'section',
  'table', 'tr', 'ul',
])
const OMITTED_TAGS = new Set(['embed', 'iframe', 'math', 'object', 'script', 'style', 'svg', 'template'])

function textFromNode(node: Node): string {
  if (node.nodeType === 3) return node.nodeValue ?? ''
  if (node.nodeType !== 1) return ''
  const tag = node.nodeName.toLowerCase()
  if (OMITTED_TAGS.has(tag)) return ''
  const content = Array.from(node.childNodes, textFromNode).join('')
  return BLOCK_TAGS.has(tag) ? `${content} ` : content
}

/** Convert provider markup to plain text before React renders it. */
export function descriptionTextFromHtml(value: string): string {
  const parsed = typeof DOMParser === 'undefined'
    ? value.replace(/<[^>]*>/gu, ' ')
    : textFromNode(new DOMParser().parseFromString(value, 'text/html').body)
  const normalized = parsed.replace(/\s+/gu, ' ').trim()
  return normalized
}

export function PublishedDescription({ variant }: { variant: InventoryVariant }) {
  const publishedDescription = variant.curatedDescription || variant.description
  const blank = publishedDescription.trim() === ''
  const source = variant.curatedDescription ? 'Кураторское описание' : 'Описание площадки'
  const reason = variant.descriptionUnavailableReason ?? 'description.not_recorded'

  return (
    <SectionCard title="Описание">
      {blank ? (
        <ValueOrReason value={null} reason={reason} />
      ) : (
        <div data-testid="published-description">
          <p data-testid="published-description-value" data-description-rendering="plain-text" className="text-xs leading-5 text-ink-2">
            {descriptionTextFromHtml(publishedDescription)}
          </p>
          <p className="mt-2 text-[11px] text-ink-muted" data-description-source={variant.curatedDescription ? 'curated' : 'description'}>
            Источник: {source}
          </p>
        </div>
      )}
    </SectionCard>
  )
}
